import {GridColDef} from "@mui/x-data-grid";
import React, {useMemo, useState} from "react";
import {UserGroupDto} from "../../../../../common/apis/usergroup/UserGroupDto";
import {Button} from "../../../../../components/button/Button";
import {ButtonLayout, NegativeButtons, PositiveButtons} from "../../../../../components/button/ButtonLayout";
import {CancelButton} from "../../../../../components/button/CancelButton";
import Dialog from "../../../../../components/dialogs/Dialog";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import DialogContent from "../../../../../components/dialogs/DialogContent";
import DialogTitle from "../../../../../components/dialogs/DialogTitle";
import Grid from "../../../../../components/dialogs/Grid";
import DataGridContainer from "../../../../../components/grid/DataGridContainer";
import ExtGridWrapper from "../../../../../components/grid/ExtGridWrapper";
import {PersistentStateId} from "../../../../../store/common/Grid";
import {t} from "../../../../../store/localization/TranslMessasge";
import {CommonTranslationKey} from "../../CommonTranslationKey";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import {useAddUserGroupMembershipsMutation, useFindAllUserGroupsQuery} from "../../usergroups/UserGroupQuery";
import {UserGroupsTranslationKey} from "../../usergroups/UserGroupsTranslationKey";
import {useFindUserGroupsByLoginQuery} from "../UserQuery";

interface UserGroupMembershipAddDialogProps {
    open: boolean;
    userLogin: string;
    onClose: () => void;
}

export default function UserGroupMembershipAddDialog({open, userLogin, onClose}: Readonly<UserGroupMembershipAddDialogProps>) {
    const {data: allGroups} = useFindAllUserGroupsQuery();
    const {data: userGroups} = useFindUserGroupsByLoginQuery(userLogin);
    const addMembershipsMutation = useAddUserGroupMembershipsMutation();

    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

    const availableGroups = useMemo(() => {
        const memberGroupIds = new Set((userGroups ?? []).map(group => group.id));
        return (allGroups ?? []).filter(group => !memberGroupIds.has(group.id));
    }, [allGroups, userGroups]);

    const columns: GridColDef<UserGroupDto>[] = useMemo(() => [
        {field: 'name', headerName: t(UserGroupsTranslationKey.NAME), width: 300},
        {field: 'description', headerName: t(UserGroupsTranslationKey.DESCRIPTION), width: 400},
    ], []);

    function addMemberships() {
        addMembershipsMutation.mutate(
                {userLogin, groupIds: selectedGroupIds},
                {
                    onSuccess: () => {
                        Snackbar.success(t(CommonTranslationKey.DATA_SAVED_SUCCESSFULLY));
                        onClose();
                    },
                    onError: (err) => {
                        Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), err);
                    },
                }
        );
    }

    const inProgress = addMembershipsMutation.isPending;

    return (
            <Dialog open={open} maxWidth="md" onClose={onClose}>
                <DialogTitle title={t(UserGroupsTranslationKey.ADD_MEMBERSHIP)} onDialogClosed={onClose}/>
                <DialogContent>
                    <Grid container>
                        <Grid item xs={12}>
                            <DataGridContainer height={"18em"}>
                                <ExtGridWrapper
                                        columns={columns}
                                        rows={availableGroups}
                                        rowCount={availableGroups.length}
                                        getRowId={row => row.id}
                                        onSelectionChanged={(selectedRowIds) => setSelectedGroupIds(selectedRowIds as string[])}
                                        autoHeight={false}
                                        peristentStateId={PersistentStateId.USER_GROUP_MEMBERSHIP_ADD_DIALOG_GRID}
                                        resourceId={"userGroupMembershipAddDialogGroups"}
                                        bulkActionsOpenedPermanently={true}/>
                            </DataGridContainer>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <ButtonLayout>
                        <NegativeButtons>
                            <CancelButton onClick={onClose} disabled={inProgress}/>
                        </NegativeButtons>
                        <PositiveButtons>
                            <Button
                                    label={t(CommonTranslationKey.ADD)}
                                    variant="contained"
                                    onClick={addMemberships}
                                    disabled={inProgress || selectedGroupIds.length === 0}
                            />
                        </PositiveButtons>
                    </ButtonLayout>
                </DialogActions>
            </Dialog>
    );
}
